import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
} from '@mui/material';
import { DataGrid, esES } from '@mui/x-data-grid';
import employeesApi from 'Api/axios/employees-api';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';
import SearchInput from 'Components/SearchInput';

const SEARCH_DEBOUNCE_MS = 350;

const styles = {
  dialog: { '& .MuiDialog-paper': { borderRadius: 2, minHeight: 480 } },
  title: { fontSize: 16, fontWeight: 600, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  primaryBtn: {
    bgcolor: colors.GREEN,
    color: '#fff',
    textTransform: 'none',
    fontWeight: 600,
    px: 2,
    '&:hover': { bgcolor: colors.GREEN_LEAF },
  },
};

export default function AddAttendeeDialog({ open, onClose, onConfirm, submitting }) {
  const { t2 } = useTranslation('EditCourseSessions');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const previousPageIds = useRef(new Set());

  useEffect(() => {
    if (!open) {
      setSearch('');
      setDebouncedSearch('');
      setUsers([]);
      setSelectedUserIds([]);
      setPage(0);
      setTotalItems(0);
    }
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchUsers = useCallback(async (term, pageNum, size) => {
    setLoading(true);
    const response = await employeesApi.getUsers({
      search: term || undefined,
      page: pageNum + 1,
      pageSize: size,
      status: 'true',
    });
    if (!response.error) {
      setUsers(response.data || []);
      setTotalItems(response.pagination?.totalItems || 0);
    } else {
      setUsers([]);
      setTotalItems(0);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      fetchUsers(debouncedSearch, page, pageSize);
    }
  }, [debouncedSearch, page, pageSize, open, fetchUsers]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setPage(0);
  }, []);

  const handleSelectionModelChange = (newSelection) => {
    const currentPageIds = new Set(users.map(u => u.id));
    const newSelectionSet = new Set(newSelection);

    const preservedSelections = selectedUserIds.filter(id => !currentPageIds.has(id));
    const currentPageSelections = newSelection.filter(id => currentPageIds.has(id));

    setSelectedUserIds([...preservedSelections, ...currentPageSelections]);
    previousPageIds.current = currentPageIds;
  };

  const currentSelectionModel = selectedUserIds.filter(id =>
    users.some(u => u.id === id)
  );

  const handleConfirm = () => {
    if (selectedUserIds.length === 0) return;
    const selectedUsers = selectedUserIds.map(id => {
      const user = users.find(u => u.id === id);
      return user || { id, fullName: '' };
    });
    onConfirm(selectedUsers);
  };

  const columns = [
    { field: 'payrollNum', headerName: t2('attendance.payrollNum'), flex: 1 },
    { field: 'fullName', headerName: t2('attendance.employeeName'), flex: 2 },
    {
      field: 'jobTitle',
      headerName: t2('attendance.jobTitle'),
      flex: 1,
      valueGetter: params => params.row.JobTitle?.name || '—',
    },
    {
      field: 'department',
      headerName: t2('attendance.department'),
      flex: 1,
      valueGetter: params => params.row.Department?.name || '—',
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={styles.dialog}>
      <DialogTitle sx={styles.title}>
        <span>{t2('attendance.addAttendeeDialogTitle')}</span>
        {selectedUserIds.length > 0 && (
          <Chip
            label={`${selectedUserIds.length} seleccionado${selectedUserIds.length !== 1 ? 's' : ''}`}
            color="primary"
            size="small"
            sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}
          />
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        <SearchInput
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t2('attendance.addAttendeeSearch')}
          autoFocus
          sx={{ mb: 2 }}
        />
        {loading && <LinearProgress sx={{ height: 2, mb: 1 }} />}
        <Box sx={{ height: 360 }}>
          <DataGrid
            density="compact"
            rows={users}
            columns={columns}
            loading={loading}
            checkboxSelection
            disableSelectionOnClick
            disableColumnMenu
            hideFooterSelectedRowCount
            selectionModel={currentSelectionModel}
            onSelectionModelChange={handleSelectionModelChange}
            paginationMode="server"
            rowCount={totalItems}
            page={page}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            sx={{
              '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold' },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: colors.GRAY_DARKER }}>
          {t2('form.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={selectedUserIds.length === 0 || submitting}
          sx={styles.primaryBtn}
          disableElevation
        >
          {t2('attendance.addAttendee')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddAttendeeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
};

AddAttendeeDialog.defaultProps = { submitting: false };
